import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { GlassPanel, Button } from '../common'
import './ImportPreview.css'

/**
 * ImportPreview - Modal showing parsed data before import
 */
export default function ImportPreview({ 
  isOpen, 
  onClose, 
  item, 
  onConfirm, 
  onSkip 
}) {
  const [editedData, setEditedData] = useState(item?.parsed || {})

  // Update editedData when item changes
  if (item?.parsed && item.parsed !== editedData._original) {
    setEditedData({ ...item.parsed, _original: item.parsed })
  }

  const handleFieldChange = (field, value) => {
    setEditedData(prev => ({ ...prev, [field]: value }))
  }

  const handleConfirm = () => {
    // Remove internal fields before confirming
    const { _original, ...data } = editedData
    onConfirm(data)
  }

  const getTypeColor = (type) => {
    switch (type) {
      case 'character': return 'var(--accent-cyan)'
      case 'shot': return 'var(--accent-amber)'
      case 'markdown': return 'var(--color-layer-synopsis)'
      default: return 'var(--text-tertiary)'
    }
  }

  const renderCharacterFields = () => (
    <>
      <div className="preview-field">
        <label>Name</label>
        <input
          type="text"
          value={editedData.name || ''}
          onChange={(e) => handleFieldChange('name', e.target.value)}
        />
      </div>
      <div className="preview-field preview-field--full">
        <label>Description</label>
        <textarea
          value={editedData.description || ''}
          onChange={(e) => handleFieldChange('description', e.target.value)}
          rows={3}
        />
      </div>
      <div className="preview-field">
        <label>Hair Style</label>
        <input
          type="text"
          value={editedData.hairStyle || ''}
          onChange={(e) => handleFieldChange('hairStyle', e.target.value)}
        />
      </div>
      <div className="preview-field">
        <label>Attire</label>
        <input
          type="text"
          value={editedData.attire || ''}
          onChange={(e) => handleFieldChange('attire', e.target.value)}
        />
      </div>
      <div className="preview-field preview-field--full">
        <label>Distinct Features</label>
        <textarea
          value={editedData.distinctFeatures || ''}
          onChange={(e) => handleFieldChange('distinctFeatures', e.target.value)}
          rows={2}
        />
      </div>
      <div className="preview-field preview-field--full">
        <label>Accessories</label>
        <input
          type="text"
          value={editedData.accessories || ''}
          onChange={(e) => handleFieldChange('accessories', e.target.value)}
        />
      </div>
    </>
  )

  const renderShotFields = () => (
    <>
      <div className="preview-field">
        <label>Shot ID</label>
        <input
          type="text"
          value={editedData.shotId || ''}
          onChange={(e) => handleFieldChange('shotId', e.target.value)}
        />
      </div>
      <div className="preview-field">
        <label>Title</label>
        <input
          type="text"
          value={editedData.title || ''}
          onChange={(e) => handleFieldChange('title', e.target.value)}
        />
      </div>
      <div className="preview-field preview-field--full">
        <label>Description</label>
        <textarea
          value={editedData.description || ''}
          onChange={(e) => handleFieldChange('description', e.target.value)}
          rows={3}
        />
      </div>
      <div className="preview-field">
        <label>Duration (seconds)</label>
        <input
          type="number"
          value={editedData.durationSeconds || 0}
          onChange={(e) => handleFieldChange('durationSeconds', parseFloat(e.target.value) || 0)}
        />
      </div>
      <div className="preview-field">
        <label>Layer</label>
        <select
          value={editedData.layer || ''}
          onChange={(e) => handleFieldChange('layer', e.target.value)}
        >
          <option value="">Select layer</option>
          <option value="A">A - 2026 Outer Frame</option>
          <option value="B">B - 1994 Cam's World</option>
          <option value="C">C - Corporate Media</option>
          <option value="D">D - Mission Tapes</option>
        </select>
      </div>
      <div className="preview-field">
        <label>Aspect Ratio</label>
        <select
          value={editedData.aspectRatio || '4:3'}
          onChange={(e) => handleFieldChange('aspectRatio', e.target.value)}
        >
          <option value="4:3">4:3</option>
          <option value="16:9">16:9</option>
          <option value="9:16">9:16</option>
        </select>
      </div>
    </>
  )

  const renderMarkdownFields = () => (
    <>
      <div className="preview-field preview-field--full">
        <label>Title</label>
        <input
          type="text"
          value={editedData.title || ''}
          onChange={(e) => handleFieldChange('title', e.target.value)}
        />
      </div>
      <div className="preview-field preview-field--full">
        <label>Content Preview</label>
        <div className="preview-markdown">
          {editedData.markdown?.slice(0, 500)}
          {editedData.markdown?.length > 500 && '...'}
        </div>
      </div>
    </>
  )

  const renderFields = () => {
    switch (item?.type) {
      case 'character':
        return renderCharacterFields()
      case 'shot':
        return renderShotFields()
      case 'markdown':
        return renderMarkdownFields()
      default:
        return (
          <div className="preview-field preview-field--full">
            <p className="preview-unknown">Unknown file type. Cannot preview.</p>
          </div>
        )
    }
  }

  return (
    <AnimatePresence>
      {isOpen && item && (
        <motion.div
          className="preview-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <motion.div
            className="preview-modal"
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            onClick={(e) => e.stopPropagation()}
          >
            <GlassPanel glow="cyan" padding="lg">
              <div className="preview-header">
                <div>
                  <h2 className="preview-title">Preview Import</h2>
                  <p className="preview-filename">{item.filename}</p>
                </div>
                <span 
                  className="preview-type-badge"
                  style={{ backgroundColor: getTypeColor(item.type) }}
                >
                  {item.type}
                </span>
              </div>

              <div className="preview-fields">
                {renderFields()}
              </div>

              <div className="preview-actions">
                <Button variant="ghost" onClick={onSkip}>
                  Skip
                </Button>
                <Button variant="outline" onClick={onClose}>
                  Cancel
                </Button>
                <Button 
                  variant="primary" 
                  onClick={handleConfirm}
                  disabled={item.type === 'unknown'}
                >
                  Import
                </Button>
              </div>
            </GlassPanel>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

